import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';

class MapFilter {
    constructor(controller) {
        this.controller = controller; // Map controller, from mapController, to call updatePositions and setTransform methods

        this.regionListForm = document.querySelector('#elt-map-region-list');
        this.countryListSelect = document.querySelector('#map-country-list');

        this.mapDataFiltersWrapper = document.querySelector('.elt-map-section-data');
        this.modalMobileFilter = document.querySelector('[data-filter-modal]');
        this.btnModalFilterApply = document.querySelector('[data-filter-apply]');
        this.btnModalFilterReset = document.querySelector('[data-filter-reset]');

        this.map = document.querySelector('#interactive-map > svg');

        this.selectConfig = {
            placeholder: true,
            placeholderValue: 'Country',
            itemSelectText: '',
            shouldSort: false,
        };

        this.allOptions = [];
        this.choices = null;
        this.viewportWidth = null;
    }

    get isFilterPopupOpen() {
        return this._isFilterPopupOpen;
    }

    set isFilterPopupOpen(value) {
        this._isFilterPopupOpen = value;

        if (value) {
            document.body.classList.add('disable-scroll');
        } else {
            document.body.classList.remove('disable-scroll');
        }
    }

    initSelect() {
        if (!this.countryListSelect) return;

        this.allOptions = Array.from(this.countryListSelect.querySelectorAll('option'))
            .filter(opt => opt.value !== '')
            .map(opt => opt.cloneNode(true))
            .sort((a, b) => a.textContent.localeCompare(b.textContent, 'en', { sensitivity: 'base' }));

        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = 'Country';
        placeholderOption.disabled = true;
        placeholderOption.selected = true;

        this.countryListSelect.innerHTML = '';
        this.countryListSelect.appendChild(placeholderOption);
        this.allOptions.forEach(opt => this.countryListSelect.appendChild(opt));

        this.choices = new Choices(this.countryListSelect, this.selectConfig);
    }

    reinitSelectByRegion(parentId) {
        if (!this.choices || !this.countryListSelect) return;

        this.choices.destroy();
        this.countryListSelect.innerHTML = '';

        const placeholderOption = document.createElement('option');
        placeholderOption.value = '';
        placeholderOption.textContent = 'Country';
        placeholderOption.disabled = true;
        placeholderOption.selected = true;
        this.countryListSelect.appendChild(placeholderOption);

        this.allOptions.forEach(opt => {
            if (opt.dataset.parent === parentId) {
                this.countryListSelect.appendChild(opt.cloneNode(true));
            }
        });

        this.choices = new Choices(this.countryListSelect, this.selectConfig);

        this.choices.removeActiveItems();
        this.choices.removeHighlightedItems();
    }

    clearMobileFilters() {
        const checkedRadio = this.regionListForm.querySelector('input[type="radio"]:checked');
        if (checkedRadio) checkedRadio.checked = false;

        this.countryListSelect.value = '';
        this.choices?.clearStore();

        this.choices?.setChoices(
            this.allOptions.map(opt => ({
                value: opt.value,
                label: opt.text,
            })),
            'value',
            'label',
            true
        );

        this.allOptions.forEach(origOption => {
            const optionInSelect = this.countryListSelect.querySelector(`option[value="${origOption.value}"]`);
            if (!optionInSelect) return;

            for (const attr of origOption.attributes) {
                if (attr.name.startsWith('data-')) {
                    optionInSelect.setAttribute(attr.name, attr.value);
                }
            }
        });

        this.choices?.setChoiceByValue('');

        this.btnModalFilterApply?.classList.remove('active');
        this.btnModalFilterReset?.classList.remove('active');
    }

    filterCountries(parentId) {
        this.reinitSelectByRegion(parentId);
    }

    hasAnyFiltersSelected() {
        const selectedOption = this.countryListSelect?.options[this.countryListSelect.selectedIndex];
        const selectedCountry = selectedOption?.value && selectedOption.value !== '';
        const selectedRadio = this.regionListForm?.querySelector('input[type="radio"]:checked');

        return Boolean(selectedCountry || selectedRadio);
    }

    showMobileResults() {
        const selectedOption = this.countryListSelect?.options[this.countryListSelect.selectedIndex];

        if (selectedOption?.dataset.url) {
            window.location.href = selectedOption.dataset.url;
            return;
        }

        const selectedRadio = this.regionListForm?.querySelector('input[type="radio"]:checked');
        if (selectedRadio) {
            this.filterCountries(selectedRadio.value);

            const regionUrl = selectedRadio.dataset.url;
            if (regionUrl) {
                window.location.href = regionUrl;
            }
        }
    }

    onRegionChange(e) {
        if (!this.regionListForm) return;

        if (e.target.matches('input[type="radio"]')) {
            const currentRegion = e.target;
            this.filterCountries(currentRegion.value);
            const regionUrl = currentRegion.dataset.url;
            if (this.isFilterPopupOpen) {
                this.btnModalFilterApply.classList.add('active');
                this.btnModalFilterReset.classList.add('active');
            } else {
                window.location.href = regionUrl;
            }
        }
    }

    onCountryChange() {
        if (!this.countryListSelect) return;

        const selectedOption = this.countryListSelect.options[this.countryListSelect.selectedIndex];
        if (!selectedOption) return;

        const regionId = selectedOption.dataset.parent;
        const countryURL = selectedOption.dataset.url;

        if(!regionId || !countryURL) return;

        if (this.isFilterPopupOpen) {
            const targetRegion = this.regionListForm.querySelector(`input[type="radio"][data-id="${CSS.escape(regionId)}"]`);
            if (!targetRegion) return;
            targetRegion.checked = true;
            this.btnModalFilterApply.classList.add('active');
            this.btnModalFilterReset.classList.add('active');
        } else {
            window.location.href = countryURL;
        }
    }

    clickHandler(e) {
        if (e.target.closest('[data-filter-open]')) {
            this.modalMobileFilter.classList.remove('closed');
            this.isFilterPopupOpen = true;
            if (this.isFilterPopupOpen && this.hasAnyFiltersSelected()) {
                if (this.btnModalFilterApply) {
                    this.btnModalFilterApply.classList.add('active');
                    this.btnModalFilterReset.classList.add('active');
                }
            }
        }

        if (this.isFilterPopupOpen && e.target.closest('[data-filter-close-modal]')) {
            this.modalMobileFilter.classList.add('closed');
            this.isFilterPopupOpen = false;
        }

        if (this.isFilterPopupOpen && e.target.closest('[data-filter-reset]')) {
            this.clearMobileFilters();
        }

        if (this.isFilterPopupOpen && e.target.closest('[data-filter-apply]')) {
            this.showMobileResults();
        }
    }

    restoreStateFromURL() {
        if (!this.regionListForm || !this.countryListSelect) return;

        const checkedRadio = this.regionListForm.querySelector('input[type="radio"]:checked');
        if (checkedRadio) {
            this.filterCountries(checkedRadio.value);
        }
    }

    handleKeyDown(e) {
        const radio = e.target.closest('.elt-map-category-radio');
        if (!radio || !this.regionListForm.contains(radio)) return;

        const radios = Array.from(this.regionListForm.querySelectorAll('.elt-map-category-radio'))
            .filter(r => !r.closest('.is-disabled'));

        const index = radios.indexOf(radio)
        if (index === -1) return

        if (['ArrowDown', 'ArrowRight'].includes(e.key)) {
            e.preventDefault()
            const next = (index + 1) % radios.length
            radios[next].focus()
        } else if (['ArrowUp', 'ArrowLeft'].includes(e.key)) {
            e.preventDefault()
            const prev = (index - 1 + radios.length) % radios.length
            radios[prev].focus()
        } else if (e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault()
            radio.checked = true
            radio.dispatchEvent(new Event('change', { bubbles: true }))

            const url = radio.dataset.url
            if (url) {
                window.location.href = url
            }
        }
    }

    init() {
        this.viewportWidth = window.innerWidth;

        if (!this.regionListForm && !this.countryListSelect) return;

        this.initSelect();

        this.restoreStateFromURL();

        this.regionListForm.addEventListener('change', (e) => this.onRegionChange(e));
        this.countryListSelect.addEventListener('change', (e) => this.onCountryChange(e));
        this.mapDataFiltersWrapper.addEventListener('click', (e) => this.clickHandler(e));
        this.regionListForm.addEventListener('keydown',(e) => this.handleKeyDown(e));
    }
}

export default MapFilter;
